import React from 'react';
import PropTypes from 'prop-types';
import Switch from '@mui/material/Switch';
import Collapse from '@mui/material/Collapse';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import SaveButton from '../../../../../components/Button/ActionButtons/SaveButton';
import commonTexts from '../texts.json';
import screenTexts from './texts.json';
import {
  ChallengeRow,
  CollapseHeader,
  Chevron,
  Row,
  SectionActions,
  StyledChip,
  StyledFormControlLabel,
  SectionDescription,
} from './styles';
import StrategyEditor from './StrategyEditor';
import { offerCurrencyForContract } from './utils';

const texts = { ...commonTexts, ...screenTexts };

/**
 * The coach's challenge contracts, each either following the default upsell
 * strategy or carrying a custom one (a snapshot of the default taken when it
 * was switched to custom). Rows are collapsed by default and saved one by one. While the pop-up A/B test is rolled out
 * to the coach, a row whose strategy (its own or the default) runs it is tagged.
 */
const ChallengeList = ({
  challengeContracts,
  challenges,
  expandedChallengeId,
  onToggleExpanded,
  onToggleUseDefault,
  onCustomTouchpointChange,
  onCustomCheckoutPreEnrollChange,
  showCheckoutPreEnroll,
  onCustomExperimentChange,
  onCustomExperimentTouchpointChange,
  showExperiment,
  isDefaultExperimentEnabled,
  onSaveOverride,
  products,
  currency,
  programs,
  mealPlans,
  isSaving,
  emptyText,
}) => {
  if (!challengeContracts.length) {
    return <SectionDescription>{emptyText || texts.noChallenges}</SectionDescription>;
  }

  return (
    <>
      {challengeContracts.map((contract) => {
        const entry = challenges[contract.id];
        const isCustom = !!entry && !entry.useDefault && !!entry.custom;
        const isExpanded = expandedChallengeId === contract.id;
        const isExperimentEnabled = isCustom ? !!entry.custom.experiment?.enabled : isDefaultExperimentEnabled;
        return (
          <ChallengeRow key={contract.id} $isExpanded={isExpanded}>
            <CollapseHeader onClick={() => onToggleExpanded(contract.id)}>
              <div>
                <StyledChip
                  size="small"
                  label={isCustom ? texts.customChip : texts.usingDefaultChip}
                  className={isCustom ? 'custom' : ''}
                />
                {showExperiment && isExperimentEnabled && (
                  <StyledChip size="small" label={texts.experimentChip} className="experiment" variant="outlined" />
                )}
                {' '}
                {contract.name || contract.productName}
              </div>
              <Row>
                <StyledFormControlLabel
                  control={(
                    <Switch
                      checked={!isCustom}
                      onChange={() => onToggleUseDefault(contract.id)}
                      color="primary"
                    />
                  )}
                  label={texts.useDefaultStrategy}
                  // The switch is its own control — clicking it must not also collapse the row.
                  onClick={(event) => event.stopPropagation()}
                />
                <Chevron $isExpanded={isExpanded}><ExpandMoreIcon fontSize="small" /></Chevron>
              </Row>
            </CollapseHeader>
            {/* unmountOnExit: a collapsed row renders nothing, so a long list stays cheap. */}
            <Collapse in={isExpanded} unmountOnExit>
              <div>
                {isCustom ? (
                  <StrategyEditor
                    strategy={entry.custom}
                    onTouchpointChange={(touchpointKey, patch) => (
                      onCustomTouchpointChange(contract.id, touchpointKey, patch)
                    )}
                    onCheckoutPreEnrollChange={(patch) => onCustomCheckoutPreEnrollChange(contract.id, patch)}
                    showCheckoutPreEnroll={showCheckoutPreEnroll}
                    onExperimentChange={(patch) => onCustomExperimentChange(contract.id, patch)}
                    onExperimentTouchpointChange={(touchpointKey, patch) => (
                      onCustomExperimentTouchpointChange(contract.id, touchpointKey, patch)
                    )}
                    showExperiment={showExperiment}
                    products={products}
                    // This challenge's own currency — its clients are billed on this contract.
                    currency={offerCurrencyForContract(contract, currency)}
                    programs={programs}
                    mealPlans={mealPlans}
                  />
                ) : (
                  <SectionDescription>{texts.usingDefaultDescription}</SectionDescription>
                )}
                <SectionActions>
                  <SaveButton onClick={() => onSaveOverride(contract.id)} disabled={isSaving} $fitToContent>
                    {texts.saveButton}
                  </SaveButton>
                </SectionActions>
              </div>
            </Collapse>
          </ChallengeRow>
        );
      })}
    </>
  );
};

ChallengeList.propTypes = {
  challengeContracts: PropTypes.array.isRequired,
  challenges: PropTypes.object.isRequired,
  expandedChallengeId: PropTypes.string,
  onToggleExpanded: PropTypes.func.isRequired,
  onToggleUseDefault: PropTypes.func.isRequired,
  onCustomTouchpointChange: PropTypes.func.isRequired,
  onCustomCheckoutPreEnrollChange: PropTypes.func,
  showCheckoutPreEnroll: PropTypes.bool,
  onCustomExperimentChange: PropTypes.func,
  onCustomExperimentTouchpointChange: PropTypes.func,
  showExperiment: PropTypes.bool,
  isDefaultExperimentEnabled: PropTypes.bool,
  onSaveOverride: PropTypes.func.isRequired,
  products: PropTypes.array.isRequired,
  currency: PropTypes.string.isRequired,
  programs: PropTypes.array.isRequired,
  mealPlans: PropTypes.array.isRequired,
  isSaving: PropTypes.bool,
  emptyText: PropTypes.string,
};

ChallengeList.defaultProps = {
  onCustomCheckoutPreEnrollChange: () => {},
  showCheckoutPreEnroll: false,
  onCustomExperimentChange: () => {},
  onCustomExperimentTouchpointChange: () => {},
  showExperiment: false,
  isDefaultExperimentEnabled: false,
  expandedChallengeId: '',
  isSaving: false,
  emptyText: '',
};

export default ChallengeList;
